import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FortnoxAccount, getFortnoxAccounts } from 'src/shared/services/accountingIntegrationService';

interface FortnoxAccountPickerProps {
  ownerId: number;
  provider: string;
  onSelect: (accountNumber: string) => void;
}

export const FortnoxAccountPicker = ({ ownerId, provider, onSelect }: FortnoxAccountPickerProps) => {
  const { t } = useTranslation(['invoice']);
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<FortnoxAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const handleToggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (!loaded) {
      setLoading(true);
      try {
        const result = await getFortnoxAccounts(ownerId, provider);
        setAccounts(result.data);
        setLoaded(true);
      } catch {
        setAccounts([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box>
      <Button variant='outlined' size='small' onClick={handleToggle} sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}>
        {t('invoice:adminInvoiceGroupAccountingTab.fetchFromFortnox')}
      </Button>
      {open && (
        <Autocomplete
          options={accounts}
          loading={loading}
          getOptionLabel={(option) => `${option.number} — ${option.description}`}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.toLowerCase();
            return options.filter((o) => o.number.toLowerCase().includes(q) || o.description.toLowerCase().includes(q));
          }}
          onChange={(_e, value) => {
            if (value) {
              onSelect(value.number);
              setOpen(false);
            }
          }}
          renderInput={(params) => (
            <TextField {...params} size='small' placeholder={t('invoice:adminInvoiceGroupAccountingTab.searchFortnoxAccount') as string} autoFocus />
          )}
          ListboxProps={{ style: { maxHeight: 250 } }}
          size='small'
          openOnFocus
          autoHighlight
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
};
